import React, { useState, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import { QuickAmounts } from "@/components/QuickAmounts";
import { storage } from "@/lib/storage";
import { formatMoney, getCurrency } from "@/lib/currencies";
import { Check, Copy, ShieldCheck } from "lucide-react";

export default function ReceiveQR() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [currency, setCurrency] = useState(storage.getCurrency());
  const [confirmed, setConfirmed] = useState(false);
  const profile = storage.getProfile() || { id: "M000000", name: "Merchant" };

  const payload = useMemo(() => {
    const data = {
      v: 1,
      type: "offline_pay",
      mId: profile.id,
      mName: profile.name,
      amt: Number(amount) || 0,
      cur: currency,
      note,
      ts: Date.now(),
    };
    return JSON.stringify(data);
  }, [amount, note, currency, profile.id, profile.name]);

  const copyCode = () => {
    const code = btoa(payload).slice(0, 14);
    navigator.clipboard?.writeText(code);
  };

  const confirmReceived = () => {
    storage.addTransaction({
      direction: "received",
      amount: Number(amount) || 0,
      currency,
      method: "qr",
      note,
      merchantId: profile.id,
    });
    setConfirmed(true);
    setTimeout(() => navigate("/merchant"), 1200);
  };

  const cur = getCurrency(currency);
  const valid = Number(amount) > 0;

  return (
    <div className="page-shell" data-testid="receive-qr-page">
      <Header title="QR Code Payment" subtitle="Customer scans to pay" />

      <div className="card-glass rounded-2xl p-5 mb-5 fade-slide-up">
        <label className="stat-label">Amount</label>
        <div className="mt-2 flex items-center gap-3">
          <span className="text-3xl font-bold text-[#E6B455]">{cur.symbol}</span>
          <input
            type="number"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="flex-1 bg-transparent text-4xl font-bold text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/25 min-w-0"
            data-testid="qr-amount-input"
          />
          <CurrencyPicker value={currency} onChange={setCurrency} compact />
        </div>
        <QuickAmounts currency={currency} onPick={setAmount} selected={amount} />
        <input
          type="text"
          placeholder="Add a note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="mt-4 w-full bg-[rgba(16,22,42,0.6)] border gold-border rounded-xl px-4 py-2.5 text-sm text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/35"
          data-testid="qr-note-input"
        />
      </div>

      <div className="card-glass gold-glow rounded-2xl p-6 flex flex-col items-center fade-slide-up" style={{ animationDelay: "0.08s" }}>
        <div className="bg-white p-4 rounded-xl" data-testid="qr-code-wrapper">
          <QRCodeSVG value={payload} size={200} level="M" />
        </div>
        <div className="mt-4 text-center">
          <div className="text-[11px] uppercase tracking-wider text-[#F7E6B4]/55">
            {profile.name} · {profile.id}
          </div>
          <div className="text-2xl font-bold gold-text mt-1" data-testid="qr-amount-display">
            {valid ? formatMoney(amount, currency) : `${cur.symbol}0.00`}
          </div>
        </div>
        <button
          onClick={copyCode}
          className="mt-4 flex items-center gap-2 text-xs text-[#E6B455] hover:underline"
          data-testid="copy-code-btn"
        >
          <Copy size={12} /> Copy short code for manual entry
        </button>
      </div>

      <div className="mt-6 fade-slide-up" style={{ animationDelay: "0.16s" }}>
        {confirmed ? (
          <div className="btn-gold rounded-xl p-4 flex items-center justify-center gap-2" data-testid="qr-confirmed">
            <Check size={18} /> Payment Received!
          </div>
        ) : (
          <button
            onClick={confirmReceived}
            disabled={!valid}
            className="btn-gold w-full py-3.5 rounded-xl font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="qr-confirm-btn"
          >
            <ShieldCheck size={16} /> Mark Payment Received
          </button>
        )}
      </div>

      <p className="text-center text-[11px] text-[#F7E6B4]/40 mt-4">
        After customer confirms payment on their device, tap above to record it offline.
      </p>
    </div>
  );
}
